import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { Lead, isPaymentRequiredError, isPlanLimitError, getErrorMessage } from '../types';
import { apiService } from '../services/api';
import { wsService, WebSocketMessage } from '../services/websocket';

interface LeadsContextType {
  leads: Lead[];
  loading: boolean;
  error: string | null;
  fetchLeads: () => Promise<void>;
  updateLeadStatus: (
    leadId: string,
    status: Lead['status'],
    statusFechamento?: Lead['statusFechamento'],
    posVendaStatus?: Lead['posVendaStatus']
  ) => Promise<void>;
  updateLead: (leadId: string, lead: Partial<Lead>) => Promise<Lead>;
  updateLeadAtendente: (leadId: string, atendenteId: string) => Promise<Lead>;
  createLead: (lead: Partial<Lead>) => Promise<Lead>;
  getLead: (leadId: string) => Lead | undefined;
  convertLead: (leadId: string) => Promise<void>;
  loseLead: (leadId: string) => Promise<void>;
  deleteLead: (leadId: string) => Promise<void>;
  /** Registra descadastro do telefone e arquiva o lead; remove da lista local como em deleteLead */
  registerLeadOptOut: (leadId: string, body?: { reason?: string }) => Promise<void>;
  updateLeadLocally: (leadId: string, updates: Partial<Lead>) => void;
  startLeadIa: (leadId: string) => Promise<Lead>;
  assumeHumanLeadIa: (leadId: string) => Promise<Lead>;
  stopLeadIa: (leadId: string) => Promise<Lead>;
  sendLeadPriceDraft: (leadId: string) => Promise<Lead>;
}

const LeadsContext = createContext<LeadsContextType | undefined>(undefined);

export const useLeads = () => {
  const context = useContext(LeadsContext);
  if (!context) {
    throw new Error('useLeads deve ser usado dentro de LeadsProvider');
  }
  return context;
};

interface LeadsProviderProps {
  children: ReactNode;
}

export const LeadsProvider: React.FC<LeadsProviderProps> = ({ children }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getLeads();
      setLeads(data);
    } catch (err) {
      setError('Erro ao carregar leads');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const updateLeadStatus = async (
    leadId: string,
    status: Lead['status'],
    statusFechamento?: Lead['statusFechamento'],
    posVendaStatus?: Lead['posVendaStatus']
  ) => {
    try {
      const updatedLead = await apiService.updateLeadStatus(leadId, status, statusFechamento, posVendaStatus);
      setLeads((prev) =>
        prev.map((lead) => (lead.leadId === leadId ? updatedLead : lead))
      );
    } catch (err) {
      setError('Erro ao atualizar status do lead');
      console.error(err);
      throw err;
    }
  };

  const updateLead = async (leadId: string, lead: Partial<Lead>): Promise<Lead> => {
    try {
      const updatedLead = await apiService.updateLead(leadId, lead);
      setLeads((prev) =>
        prev.map((l) => (l.leadId === leadId ? updatedLead : l))
      );
      return updatedLead;
    } catch (err) {
      setError('Erro ao atualizar lead');
      console.error(err);
      throw err;
    }
  };

  const updateLeadAtendente = async (leadId: string, atendenteId: string): Promise<Lead> => {
    try {
      const updatedLead = await apiService.updateLeadAtendente(leadId, atendenteId);
      setLeads((prev) =>
        prev.map((l) => (l.leadId === leadId ? updatedLead : l))
      );
      return updatedLead;
    } catch (err) {
      setError('Erro ao atualizar atendente do lead');
      console.error(err);
      throw err;
    }
  };

  const createLead = async (lead: Partial<Lead>): Promise<Lead> => {
    try {
      const newLead = await apiService.createLead(lead);
      setLeads((prev) => [...prev, newLead]);
      return newLead;
    } catch (err: any) {
      // Set specific error message for payment/limit errors
      if (isPaymentRequiredError(err)) {
        setError('PAYMENT_REQUIRED:' + getErrorMessage(err));
      } else if (isPlanLimitError(err)) {
        setError('PLAN_LIMIT:' + getErrorMessage(err));
      } else {
        setError('Erro ao criar lead');
      }
      console.error(err);
      throw err;
    }
  };

  const getLead = (leadId: string): Lead | undefined => {
    return leads.find((lead) => lead.leadId === leadId);
  };

  const convertLead = async (leadId: string): Promise<void> => {
    try {
      const updatedLead = await apiService.updateLeadStatus(
        leadId,
        'fechamento',
        'vendido',
        'conversoes_ganhas'
      );
      setLeads((prev) =>
        prev.map((lead) => (lead.leadId === leadId ? updatedLead : lead))
      );
    } catch (err) {
      setError('Erro ao converter lead');
      console.error(err);
      throw err;
    }
  };

  const loseLead = async (leadId: string): Promise<void> => {
    try {
      const updatedLead = await apiService.updateLeadStatus(leadId, 'fechamento', 'nao_vendido');
      setLeads((prev) =>
        prev.map((lead) => (lead.leadId === leadId ? updatedLead : lead))
      );
    } catch (err) {
      setError('Erro ao marcar lead como perdido');
      console.error(err);
      throw err;
    }
  };

  const deleteLead = async (leadId: string): Promise<void> => {
    try {
      await apiService.deleteLead(leadId);
      // Remove from local state (or filter out 'excluido' leads)
      setLeads((prev) => prev.filter((lead) => lead.leadId !== leadId));
    } catch (err) {
      setError('Erro ao excluir lead');
      console.error(err);
      throw err;
    }
  };

  const registerLeadOptOut = async (leadId: string, body?: { reason?: string }): Promise<void> => {
    try {
      await apiService.registerLeadOptOut(leadId, body);
      setLeads((prev) => prev.filter((lead) => lead.leadId !== leadId));
    } catch (err) {
      setError('Erro ao registrar descadastro do lead');
      console.error(err);
      throw err;
    }
  };

  // Update a lead locally (used for WebSocket updates)
  const updateLeadLocally = useCallback((leadId: string, updates: Partial<Lead>) => {
    setLeads((prev) =>
      prev.map((lead) => (lead.leadId === leadId ? { ...lead, ...updates } : lead))
    );
  }, []);

  const startLeadIa = async (leadId: string): Promise<Lead> => {
    const updated = await apiService.startLeadIa(leadId);
    setLeads((prev) => prev.map((l) => (l.leadId === leadId ? updated : l)));
    return updated;
  };

  const assumeHumanLeadIa = async (leadId: string): Promise<Lead> => {
    const updated = await apiService.assumeHumanLeadIa(leadId);
    setLeads((prev) => prev.map((l) => (l.leadId === leadId ? updated : l)));
    return updated;
  };

  const stopLeadIa = async (leadId: string): Promise<Lead> => {
    const updated = await apiService.stopLeadIa(leadId);
    setLeads((prev) => prev.map((l) => (l.leadId === leadId ? updated : l)));
    return updated;
  };

  const sendLeadPriceDraft = async (leadId: string): Promise<Lead> => {
    const updated = await apiService.sendLeadPriceDraft(leadId);
    setLeads((prev) => prev.map((l) => (l.leadId === leadId ? updated : l)));
    return updated;
  };

  // Espelho mutável de `leads` pra ler dentro do callback WS sem listar `leads`
  // nas deps — listar invalidaria o callback a cada `setLeads` e re-rodaria o
  // useEffect abaixo (off+on no wsService), churn desnecessário.
  const leadsRef = useRef<Lead[]>([]);
  useEffect(() => {
    leadsRef.current = leads;
  }, [leads]);

  // Throttle pra refetch de leads desconhecidos no WS. PR #72 simplesmente
  // ignorava lead_updated com leadId fora da lista pra matar o storm — efeito
  // colateral: leads NOVAS (recém-criadas via webhook) só apareciam em refresh
  // manual. Agora: agenda 1 refetch dentro de 4s e respeita gap mínimo de 30s
  // entre refetches. N eventos pra leads desconhecidos consolidam em 1 fetch
  // — sem storm, mas leads novas aparecem rápido.
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFetchAtRef = useRef<number>(0);
  const scheduleRefetchForUnknownLead = useCallback(() => {
    if (refetchTimerRef.current) return;
    const MIN_DELAY_MS = 4000;
    const MIN_GAP_MS = 30000;
    const sinceLast = Date.now() - lastFetchAtRef.current;
    const delay = Math.max(MIN_DELAY_MS, MIN_GAP_MS - sinceLast);
    refetchTimerRef.current = setTimeout(() => {
      refetchTimerRef.current = null;
      lastFetchAtRef.current = Date.now();
      fetchLeads().catch((error) => {
        console.error('Erro ao recarregar leads (refetch agendado):', error);
      });
    }, delay);
  }, [fetchLeads]);

  useEffect(() => {
    return () => {
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = null;
      }
    };
  }, []);

  // Listen for WebSocket lead_updated events.
  // Patch local-only: o backend manda o lead já com `unreadCount` atualizado, e
  // a tela de detalhe (LeadDetails.tsx) faz `apiService.getLead` no mount —
  // não precisamos ir ao DB aqui pro caso 'lead conhecido'.
  const handleLeadUpdated = useCallback((wsMessage: WebSocketMessage) => {
    if (wsMessage.type !== 'lead_updated' || !wsMessage.leadId || !wsMessage.data) return;
    const updates = wsMessage.data as Partial<Lead>;
    const leadExists = leadsRef.current.some((lead) => lead.leadId === wsMessage.leadId);
    if (leadExists) {
      updateLeadLocally(wsMessage.leadId, updates);
    } else {
      // Lead desconhecida (provavelmente criada agora via webhook ou outro
      // atendente). Agenda refetch throttled em vez de ignorar — UX melhor
      // sem reabrir a porta pro storm de polling.
      scheduleRefetchForUnknownLead();
    }
  }, [updateLeadLocally, scheduleRefetchForUnknownLead]);

  // Subscribe to WebSocket lead_updated events
  useEffect(() => {
    wsService.on('lead_updated', handleLeadUpdated);
    return () => {
      wsService.off('lead_updated', handleLeadUpdated);
    };
  }, [handleLeadUpdated]);

  const value: LeadsContextType = {
    leads,
    loading,
    error,
    fetchLeads,
    updateLeadStatus,
    updateLead,
    updateLeadAtendente,
    createLead,
    getLead,
    convertLead,
    loseLead,
    deleteLead,
    registerLeadOptOut,
    updateLeadLocally,
    startLeadIa,
    assumeHumanLeadIa,
    stopLeadIa,
    sendLeadPriceDraft,
  };

  return <LeadsContext.Provider value={value}>{children}</LeadsContext.Provider>;
};
